import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { reviews, faqs } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_URL = 'https://readdy.ai/api/form/d9otraiqs38sj6jc7fq0';

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('loading');

    try {
      const fd = new FormData(e.currentTarget);
      const honeypot = fd.get('phone_alt');
      if (honeypot && String(honeypot).trim() !== '') {
        setFormStatus('success');
        setFormMessage('消息已发送！我们会尽快回复您。');
        setFormData({ name: '', email: '', message: '' });
        return;
      }

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: fd.get('name') as string,
          email: fd.get('email') as string,
          message: fd.get('message') as string,
        }).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('消息已发送！我们会尽快回复您。');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setFormStatus('error');
        setFormMessage(parsed?.meta?.message || '发送失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫网',
    description: '韩漫网是专业的韩国漫画在线阅读平台，提供免费韩国漫画全集，支持下拉式浏览，收录热门韩漫、最新韩漫、完结韩漫等丰富分类。',
    url: 'https://hanmanwang.com/about',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <main className="pt-16">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="bg-background-50 py-10 md:py-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center mb-14 animate-fadeInUp">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于韩漫网
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                韩漫网致力于为广大漫画爱好者提供优质的韩国漫画在线阅读服务。
                我们收录了丰富的韩国漫画作品，支持下拉式浏览模式，
                让您随时随地沉浸在最精彩的韩漫世界中。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-16">
              {[
                { icon: 'ri-book-read-line', title: '丰富资源', desc: '收录上千部韩国漫画作品，涵盖恋爱、奇幻、动作、悬疑等多种类型' },
                { icon: 'ri-smartphone-line', title: '流畅阅读', desc: '支持下拉式阅读模式，适配手机、平板和电脑，随时随地畅读漫画' },
                { icon: 'ri-flashlight-line', title: '极速更新', desc: '热门作品第一时间同步更新，让您追更不等待，享受最新鲜的阅读体验' },
                { icon: 'ri-shield-check-line', title: '完全免费', desc: '所有漫画作品均免费阅读，无隐藏收费，让阅读回归纯粹' },
              ].map((item, i) => (
                <div
                  key={i}
                  className="text-center p-6 bg-background-100 rounded-lg border border-background-200/70 animate-fadeInUp"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <span className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-100">
                    <i className={`${item.icon} text-xl text-primary-600`}></i>
                  </span>
                  <h3 className="text-base font-heading font-semibold text-foreground-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500">看看我们的读者怎么说</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6 animate-fadeInUp"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }, (_, i) => (
                      <span key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className={`ri-star-fill text-sm ${i < review.rating ? 'text-accent-500' : 'text-foreground-300'}`}></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground-800">{review.name}</span>
                    <span className="text-xs text-foreground-400">{review.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-10 md:py-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于韩漫网的常见疑问解答</p>
            </div>
            <div className="max-w-2xl mx-auto space-y-3">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-200/50 transition-colors"
                  >
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 animate-fadeIn">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">
                  有任何问题或建议？欢迎通过以下方式联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
                  <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </span>
                  <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                  <p className="text-sm font-medium text-foreground-800">contact@hanmanwang.com</p>
                </div>
                <div className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
                  <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100">
                    <i className="ri-wechat-line text-lg text-accent-600"></i>
                  </span>
                  <p className="text-xs text-foreground-500 mb-1">微信公众号</p>
                  <p className="text-sm font-medium text-foreground-800">韩漫网官方</p>
                </div>
                <div className="text-center p-5 bg-background-50 rounded-lg border border-background-200/70">
                  <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-secondary-100">
                    <i className="ri-time-line text-lg text-secondary-600"></i>
                  </span>
                  <p className="text-xs text-foreground-500 mb-1">工作时间</p>
                  <p className="text-sm font-medium text-foreground-800">周一至周五 9:00-18:00</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8" data-readdy-form>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 resize-none"
                    placeholder="请输入您的留言（500字以内）"
                  />
                  <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {formStatus === 'loading' ? '发送中...' : '发送消息'}
                </button>
                {formStatus === 'success' && (
                  <p className="mt-3 text-sm text-green-600 text-center animate-fadeIn">{formMessage}</p>
                )}
                {formStatus === 'error' && (
                  <p className="mt-3 text-sm text-red-600 text-center animate-fadeIn">{formMessage}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}